<template>
  <div class="stripe-checkout-form">
    <div v-if="!hasKey" class="stripe-checkout-form__missing">
      <p class="stripe-checkout-form__missing-title">
        Stripe publishable key is missing
      </p>
      <p class="stripe-checkout-form__missing-text">
        Add
        <code>VUE_APP_STRIPE_PUBLISHABLE_KEY</code>
        to your
        <code>.env</code>
        file (use a
        <code>pk_test_...</code>
        key for UI-only testing), then restart the dev server.
      </p>
    </div>

    <template v-else>
      <div v-if="isLoading" class="stripe-checkout-form__loading">
        Loading Stripe checkout...
      </div>

      <div v-show="!isLoading" class="stripe-checkout-form__body">
        <div
          ref="expressCheckoutRef"
          class="stripe-checkout-form__express"
        ></div>

        <div v-if="showExpressDivider" class="stripe-checkout-form__or">
          <span>Or</span>
        </div>

        <p class="stripe-checkout-form__section-title">Contact Info</p>
        <div ref="linkAuthRef" class="stripe-checkout-form__element"></div>

        <div class="stripe-checkout-form__divider"></div>

        <p class="stripe-checkout-form__section-title">Payment Method</p>
        <div ref="paymentRef" class="stripe-checkout-form__element"></div>

        <div class="stripe-checkout-form__divider"></div>

        <p class="stripe-checkout-form__section-title">Shipping details</p>
        <div ref="addressRef" class="stripe-checkout-form__element"></div>

        <p v-if="errorMessage" class="stripe-checkout-form__error">
          {{ errorMessage }}
        </p>
      </div>
    </template>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import {
  getStripe,
  hasStripePublishableKey,
  toStripeAmount,
} from "@/services/stripeService";

const props = defineProps({
  amount: {
    type: Number,
    required: true,
  },
  currency: {
    type: String,
    default: "usd",
  },
  prefill: {
    type: Object,
    default: () => ({}),
  },
  clientSecret: {
    type: String,
    default: "",
  },
  preparePayment: {
    type: Function,
    default: null,
  },
  prepareShippingRates: {
    type: Function,
    default: null,
  },
  shippingRates: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "ready",
  "error",
  "payment-success",
  "shipping-change",
]);

const hasKey = hasStripePublishableKey();
const isLoading = ref(true);
const errorMessage = ref("");
const showExpressDivider = ref(false);
const latestEmail = ref("");

const expressCheckoutRef = ref(null);
const linkAuthRef = ref(null);
const paymentRef = ref(null);
const addressRef = ref(null);

let stripe = null;
let elements = null;
let expressCheckoutElement = null;
let linkAuthElement = null;
let paymentElement = null;
let addressElement = null;
let isConfirming = false;

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#003d6a",
    colorBackground: "#ffffff",
    colorText: "#3b4648",
    colorDanger: "#e00069",
    fontFamily: "League Spartan, system-ui, sans-serif",
    borderRadius: "8px",
  },
};

const toStripeCountry = (country) => {
  const value = String(country || "")
    .trim()
    .toLowerCase();
  if (!value || value.includes("united states") || value === "us") {
    return "US";
  }
  if (value.includes("singapore") || value === "sg") {
    return "SG";
  }
  return value.length === 2 ? value.toUpperCase() : "US";
};

const getAddressDefaultValues = () => {
  const data = props.prefill || {};
  const name = data.attention_to || data.organization_name || "";
  const line1 = data.address || "";
  const city = data.city || "";
  const state = data.state || "";
  const postalCode = data.zip || data.postal_code || "";

  if (!name && !line1 && !city && !state && !postalCode) {
    return undefined;
  }

  return {
    name,
    phone: data.phone || undefined,
    address: {
      line1,
      line2: data.address_additional || "",
      city,
      state,
      postal_code: postalCode,
      country: toStripeCountry(data.country),
    },
  };
};

const emitShippingChange = (address = {}) => {
  emit("shipping-change", {
    postalCode: address.postal_code || "",
    country: address.country || "",
    city: address.city || "",
    state: address.state || "",
  });
};

const bindAddressChangeHandler = () => {
  if (!addressElement) return;

  addressElement.on("change", (event) => {
    emitShippingChange(event?.value?.address || {});
  });
};

const optionalText = (value) => {
  const text = String(value || "").trim();
  return text || undefined;
};

const getBillingDetails = (details) => ({
  email: optionalText(details.email),
  name: optionalText(details.customerName),
  phone: optionalText(details.phone),
  address: {
    line1: optionalText(details.shippingAddress),
    line2: optionalText(details.shippingAddressAdditional),
    city: optionalText(details.shippingCity),
    state: optionalText(details.shippingState),
    postal_code: optionalText(details.shippingZip),
    country: toStripeCountry(details.country || details.shippingCountry),
  },
});

const fromStripeCountry = (country) => {
  const value = String(country || "").trim();
  if (!value) {
    return "United States";
  }
  const upper = value.toUpperCase();
  if (upper === "US" || value.toLowerCase().includes("united states")) {
    return "United States";
  }
  if (upper === "SG" || value.toLowerCase().includes("singapore")) {
    return "Singapore";
  }
  return value;
};

const getDraftValues = async (expressEvent) => {
  const expressShipping = expressEvent?.shippingAddress;
  const expressBilling = expressEvent?.billingDetails;
  let addressValue = null;

  if (addressElement) {
    try {
      const result = await addressElement.getValue();
      addressValue = result?.value || null;
    } catch (error) {
      console.warn("Failed to read Stripe address:", error);
    }
  }

  const address =
    expressShipping?.address ||
    addressValue?.address ||
    expressBilling?.address ||
    {};
  const country = fromStripeCountry(address.country || props.prefill?.country);

  return {
    email:
      expressBilling?.email || latestEmail.value || props.prefill?.email || "",
    phone:
      addressValue?.phone ||
      expressBilling?.phone ||
      props.prefill?.phone ||
      "",
    customerName:
      addressValue?.name ||
      expressShipping?.name ||
      expressBilling?.name ||
      props.prefill?.attention_to ||
      props.prefill?.organization_name ||
      "",
    shippingAddress: address.line1 || "",
    shippingAddressAdditional: address.line2 || "",
    shippingCity: address.city || "",
    shippingState: address.state || "",
    shippingZip: address.postal_code || "",
    shippingCountry: country,
    country,
  };
};

const getCheckoutValues = async (expressEvent) => {
  const expressShipping = expressEvent?.shippingAddress;
  const expressBilling = expressEvent?.billingDetails;

  let addressComplete = Boolean(expressShipping);
  let addressValue = null;

  if (addressElement) {
    try {
      const result = await addressElement.getValue();
      addressComplete = addressComplete || Boolean(result?.complete);
      addressValue = result?.value || null;
    } catch (error) {
      console.warn("Failed to read Stripe address:", error);
    }
  }

  const address =
    expressShipping?.address ||
    addressValue?.address ||
    expressBilling?.address ||
    {};

  if (!addressComplete) {
    return {
      complete: false,
      error: "Please complete shipping details.",
    };
  }

  const shippingZip = address.postal_code || props.prefill?.zip || "";

  if (!String(shippingZip).trim()) {
    return {
      complete: false,
      error: "Please enter a ZIP code in shipping details.",
    };
  }

  const country = fromStripeCountry(address.country || props.prefill?.country);

  return {
    complete: true,
    email:
      expressBilling?.email || latestEmail.value || props.prefill?.email || "",
    phone:
      addressValue?.phone ||
      expressBilling?.phone ||
      props.prefill?.phone ||
      "",
    customerName:
      addressValue?.name ||
      expressShipping?.name ||
      expressBilling?.name ||
      props.prefill?.attention_to ||
      props.prefill?.organization_name ||
      "",
    shippingAddress: address.line1 || "",
    shippingAddressAdditional: address.line2 || "",
    shippingCity: address.city || "",
    shippingState: address.state || "",
    shippingZip,
    shippingCountry: country,
    country,
  };
};

const getElementsOptions = () => {
  if (props.clientSecret) {
    return {
      clientSecret: props.clientSecret,
      appearance,
    };
  }

  return {
    mode: "payment",
    amount: toStripeAmount(props.amount),
    currency: props.currency,
    appearance,
    paymentMethodCreation: "manual",
  };
};

const confirmPayment = async (expressEvent) => {
  if (!stripe || !elements) {
    return { success: false, error: "Stripe is not ready yet." };
  }

  if (!props.clientSecret) {
    return {
      success: false,
      error: "PaymentIntent is not ready yet. Please try again.",
    };
  }

  const { error: submitError } = await elements.submit();
  if (submitError) {
    errorMessage.value = submitError.message || "Please complete the form.";
    return { success: false, error: errorMessage.value };
  }

  isConfirming = true;
  try {
    const details = await getCheckoutValues(expressEvent);
    if (!details.complete) {
      errorMessage.value = details.error || "Please complete the form.";
      return { success: false, error: errorMessage.value };
    }

    if (typeof props.preparePayment === "function") {
      const prepared = await props.preparePayment(details);
      if (prepared && prepared.success === false) {
        errorMessage.value =
          prepared.error || "Failed to update payment details.";
        return { success: false, error: errorMessage.value };
      }
    }

    const returnUrl = `${window.location.origin}/makercart/instant-checkout`;
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: props.clientSecret,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: getBillingDetails(details),
        },
      },
      redirect: "if_required",
    });

    if (error) {
      errorMessage.value = error.message || "Payment failed. Please try again.";
      return { success: false, error: errorMessage.value };
    }

    errorMessage.value = "";
    emit("payment-success", paymentIntent);
    return { success: true, paymentIntent, error: null };
  } finally {
    isConfirming = false;
  }
};

const mountElements = async () => {
  if (!hasKey) {
    isLoading.value = false;
    return;
  }

  try {
    isLoading.value = true;
    errorMessage.value = "";

    stripe = await getStripe();
    if (!stripe) {
      throw new Error("Unable to initialize Stripe.js");
    }

    elements = stripe.elements(getElementsOptions());

    expressCheckoutElement = elements.create("expressCheckout", {
      buttonType: {
        link: "pay",
      },
    });
    expressCheckoutElement.mount(expressCheckoutRef.value);
    expressCheckoutElement.on("ready", ({ availablePaymentMethods }) => {
      showExpressDivider.value = Boolean(
        availablePaymentMethods &&
          Object.values(availablePaymentMethods).some(Boolean)
      );
    });
    expressCheckoutElement.on("click", ({ resolve }) => {
      const rates = Array.isArray(props.shippingRates)
        ? props.shippingRates
        : [];
      resolve({
        emailRequired: true,
        phoneNumberRequired: true,
        shippingAddressRequired: true,
        ...(rates.length ? { shippingRates: rates } : {}),
      });
    });
    expressCheckoutElement.on("shippingaddresschange", async (event) => {
      const postal = event.address?.postal_code || "";
      emitShippingChange({
        postal_code: postal,
        country: event.address?.country || "",
        city: event.address?.city || "",
        state: event.address?.state || "",
      });

      if (typeof props.prepareShippingRates === "function") {
        try {
          const rates = await props.prepareShippingRates({
            postalCode: postal,
            country: event.address?.country || "",
          });
          if (!Array.isArray(rates) || !rates.length) {
            event.reject();
            return;
          }
          event.resolve({ shippingRates: rates });
          return;
        } catch (error) {
          console.warn("Failed to prepare Express shipping rates:", error);
          event.reject();
          return;
        }
      }

      const rates = Array.isArray(props.shippingRates)
        ? props.shippingRates
        : [];
      event.resolve(rates.length ? { shippingRates: rates } : undefined);
    });
    expressCheckoutElement.on("confirm", async (event) => {
      const result = await confirmPayment(event);
      if (!result.success) {
        emit("error", result.error);
      }
    });

    latestEmail.value = props.prefill?.email || latestEmail.value || "";
    const linkDefaults = latestEmail.value
      ? { defaultValues: { email: latestEmail.value } }
      : {};
    linkAuthElement = elements.create("linkAuthentication", linkDefaults);
    linkAuthElement.mount(linkAuthRef.value);
    linkAuthElement.on("change", (event) => {
      if (event?.value?.email) {
        latestEmail.value = event.value.email;
      }
    });

    paymentElement = elements.create("payment", {
      layout: {
        type: "accordion",
        defaultCollapsed: false,
        radios: "always",
        spacedAccordionItems: true,
      },
      fields: {
        billingDetails: {
          email: "never",
          address: {
            country: "never",
            postalCode: "never",
          },
        },
      },
    });
    paymentElement.mount(paymentRef.value);

    const addressDefaults = getAddressDefaultValues();
    addressElement = elements.create("address", {
      mode: "shipping",
      fields: {
        phone: "always",
      },
      validation: {
        phone: {
          required: "never",
        },
      },
      ...(addressDefaults ? { defaultValues: addressDefaults } : {}),
    });
    addressElement.mount(addressRef.value);
    bindAddressChangeHandler();

    emit("ready", { stripe, elements });
  } catch (error) {
    console.error("Failed to mount Stripe Elements:", error);
    errorMessage.value = error?.message || "Failed to load Stripe checkout UI.";
    emit("error", errorMessage.value);
  } finally {
    isLoading.value = false;
  }
};

const destroyElements = () => {
  try {
    expressCheckoutElement?.unmount();
    linkAuthElement?.unmount();
    paymentElement?.unmount();
    addressElement?.unmount();
  } catch (error) {
    console.warn("Error unmounting Stripe Elements:", error);
  }

  expressCheckoutElement = null;
  linkAuthElement = null;
  paymentElement = null;
  addressElement = null;
  elements = null;
};

/**
 * Validates Stripe fields without confirming payment.
 */
const validate = async () => {
  if (!elements) {
    return { complete: false, error: "Stripe is not ready yet." };
  }

  const { error: submitError } = await elements.submit();
  if (submitError) {
    errorMessage.value = submitError.message || "Please complete the form.";
    return { complete: false, error: errorMessage.value };
  }

  errorMessage.value = "";
  return { complete: true, error: null, elements, stripe };
};

defineExpose({
  validate,
  confirmPayment,
  getCheckoutValues,
  getDraftValues,
  getStripeInstance: () => stripe,
  getElements: () => elements,
});

watch(
  () => props.amount,
  (amount) => {
    if (!elements || props.clientSecret) return;
    elements.update({
      amount: toStripeAmount(amount),
    });
  }
);

watch(
  () => props.clientSecret,
  async (secret, previousSecret) => {
    if (!secret || secret === previousSecret || isConfirming) return;
    destroyElements();
    await mountElements();
  }
);

onMounted(() => {
  mountElements();
});

onBeforeUnmount(() => {
  destroyElements();
});
</script>

<style scoped>
@import "@/assets/css/colors.css";

.stripe-checkout-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stripe-checkout-form__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.stripe-checkout-form__loading,
.stripe-checkout-form__missing {
  width: 100%;
  padding: 16px;
  border-radius: 12px;
  background: #f5f7fa;
  border: 1px solid #d8dae5;
}

.stripe-checkout-form__missing-title,
.stripe-checkout-form__loading {
  margin: 0 0 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 700;
  color: var(--primary-blue);
}

.stripe-checkout-form__missing-text {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--black-body-text);
}

.stripe-checkout-form__missing-text code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  background: #eef1f6;
  padding: 1px 4px;
  border-radius: 4px;
}

.stripe-checkout-form__section-title {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-big);
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: var(--black-body-text);
}

.stripe-checkout-form__divider {
  width: 100%;
  border-top: 1px dashed #d0d5dd;
}

.stripe-checkout-form__or {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6c737f;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
}

.stripe-checkout-form__or::before,
.stripe-checkout-form__or::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #d8dae5;
}

.stripe-checkout-form__element {
  width: 100%;
  min-height: 24px;
}

.stripe-checkout-form__express {
  width: 100%;
  min-height: 0;
}

.stripe-checkout-form__error {
  margin: 0;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #e00069;
}
</style>
